<script setup lang="ts">
/**
 * "Generate invoice" — a manual invoice for a real-money deposit (or,
 * disabled for now, a withdrawal), issued straight from the admin panel
 * instead of by XLSX bulk-import or a real purchase.
 *
 * Posts to `POST /api/admin/invoices-ext/generate`
 * (`backend/src/game/admin/admin-invoice-gen.controller.ts`) — a game-owned
 * supplement beside core's own `POST /api/admin/invoices/generate`, carrying
 * the three fields core's route has no room for: `transactionId`
 * (auto-generated server-side when left blank), `status` and `itemCode`.
 * See `ADMIN_API_CHANGELOG.md`'s 2026-09-30 entry.
 *
 * ── The five packs are duplicated by hand, not imported ─────────────────────
 *
 * Same small-duplication precedent `CreateUserModal.vue` already sets for its
 * KYC option list: the admin bundle has no reason to pull in
 * `game/economy/premium.data.ts`, a backend-only file. Source of truth stays
 * there; this list exists only to prefill the amount/description/diamonds a
 * pack invoice carries. If a pack is ever added or repriced, update both.
 *
 * ── Currency is fixed to EUR ─────────────────────────────────────────────────
 *
 * Every fixed pack and the custom-amount rate are priced in EUR server-side
 * (`CUSTOM_PACK.currency` / `DiamondPack.currency` in `premium.data.ts`) — the
 * game has no other currency to sell in, so there is no selector offering one.
 */
import { computed, ref, watch } from 'vue';

import { adminApi } from './admin-api';
import AdminModal from './AdminModal.vue';
import { ApiError } from '@/net/api';

const emit = defineEmits<{ close: []; created: [] }>();

// ─── Fixed packs (mirrors backend/src/game/economy/premium.data.ts) ─────────

interface PackOption {
  id: string;
  name: string;
  diamonds: number;
  bonusDiamonds: number;
  priceCents: number;
}

const PACKS: PackOption[] = [
  { id: 'pack_field', name: 'Field Requisition', diamonds: 250, bonusDiamonds: 0, priceCents: 500 },
  { id: 'pack_supply', name: 'Supply Column', diamonds: 450, bonusDiamonds: 0, priceCents: 899 },
  { id: 'pack_depot', name: 'Depot Contract', diamonds: 1000, bonusDiamonds: 0, priceCents: 1999 },
  { id: 'pack_arsenal', name: 'Arsenal Allocation', diamonds: 2250, bonusDiamonds: 0, priceCents: 4599 },
  { id: 'pack_warchest', name: 'War Chest', diamonds: 4500, bonusDiamonds: 0, priceCents: 8999 },
];

/** The custom-amount slider's own rate — `CUSTOM_PACK.rateCentsPer100`. */
const CUSTOM_RATE_CENTS_PER_100 = 200;

/** The only currency the shop sells in — see the header comment. */
const CURRENCY = 'EUR';
const PAYMENT_METHODS = [
  'Credit Card',
  'Credit Card (Visa)',
  'Credit Card (MasterCard)',
  'Bank Transfer',
  'Mock Payment',
];
const VAT_RATES = [0, 20, 22];

// ─── Invoice type ────────────────────────────────────────────────────────────

const type = ref<'IN' | 'OUT'>('IN');

// ─── User search ─────────────────────────────────────────────────────────────

interface FoundUser {
  id: string;
  email: string;
  username: string | null;
}

const query = ref('');
const results = ref<FoundUser[]>([]);
const searching = ref(false);
const selected = ref<FoundUser | null>(null);
let debounceTimer: ReturnType<typeof setTimeout> | undefined;

watch(query, (value) => {
  clearTimeout(debounceTimer);
  if (selected.value || value.trim().length < 2) {
    results.value = [];
    return;
  }
  debounceTimer = setTimeout(() => void runSearch(value.trim()), 300);
});

async function runSearch(term: string): Promise<void> {
  searching.value = true;
  try {
    const page = await adminApi.players({ search: term, per_page: 8 });
    results.value = page.items.map((u) => ({ id: u.id, email: u.email, username: u.username }));
  } catch {
    results.value = [];
  } finally {
    searching.value = false;
  }
}

function pick(user: FoundUser): void {
  selected.value = user;
  query.value = '';
  results.value = [];
}

function clearSelection(): void {
  selected.value = null;
  query.value = '';
}

// ─── "Can't find them? Create one" ──────────────────────────────────────────

const creatingUser = ref(false);
const newUserName = ref('');
const newUserEmail = ref('');
const newUserPassword = ref('');
const createUserError = ref<string | null>(null);
const savingNewUser = ref(false);

function deriveUsername(email: string): string {
  return (email.split('@')[0] || 'player').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 24) || 'player';
}

async function createUser(): Promise<void> {
  const email = newUserEmail.value.trim();
  if (!email) {
    createUserError.value = 'Email is required.';
    return;
  }
  if (newUserPassword.value.length < 6) {
    createUserError.value = 'Password must be at least 6 characters.';
    return;
  }
  savingNewUser.value = true;
  createUserError.value = null;
  try {
    const created = await adminApi.createUser({
      username: newUserName.value.trim() || deriveUsername(email),
      email,
      password: newUserPassword.value,
      emailVerified: true,
    });
    selected.value = { id: created.id, email: created.email, username: created.username };
    creatingUser.value = false;
    newUserName.value = '';
    newUserEmail.value = '';
    newUserPassword.value = '';
  } catch (caught) {
    createUserError.value =
      caught instanceof ApiError ? caught.message || `Request failed (${caught.status}).` : 'Could not reach the server.';
  } finally {
    savingNewUser.value = false;
  }
}

// ─── Payment method ──────────────────────────────────────────────────────────

const paymentMethod = ref(PAYMENT_METHODS[0]);

// ─── Package vs custom amount ────────────────────────────────────────────────

const selectedPackId = ref<string | 'custom'>('pack_field');
const amountExcl = ref((PACKS[0].priceCents / 100).toFixed(2));
const vatRate = ref(0);
const description = ref(PACKS[0].name);

const selectedPack = computed(() => PACKS.find((p) => p.id === selectedPackId.value) ?? null);

function pickPack(pack: PackOption): void {
  selectedPackId.value = pack.id;
  amountExcl.value = (pack.priceCents / 100).toFixed(2);
  description.value = pack.name;
}

function pickCustom(): void {
  selectedPackId.value = 'custom';
}

const amountExclCents = computed(() => Math.round((parseFloat(amountExcl.value) || 0) * 100));
const vatCents = computed(() => Math.round((amountExclCents.value * vatRate.value) / 100));
const totalCents = computed(() => amountExclCents.value + vatCents.value);
const amountIncl = computed({
  get: () => (totalCents.value / 100).toFixed(2),
  set: (value: string) => {
    selectedPackId.value = 'custom';
    const inclCents = Math.round((parseFloat(value) || 0) * 100);
    amountExcl.value = (inclCents / (1 + vatRate.value / 100) / 100).toFixed(2);
  },
});

/** Cents an arbitrary diamond count costs at the custom slider's own rate. */
function diamondsToCents(count: number): number {
  return Math.round((count / 100) * CUSTOM_RATE_CENTS_PER_100);
}

/**
 * The diamond count this amount is worth — the pack's own fixed total, or,
 * for a custom amount, the amount's own rate-derived count. Writable: typing
 * a diamond count directly drops the dialog to a custom amount and prices
 * that count at the same rate, the same way typing an amount does.
 */
const diamonds = computed({
  get: () => {
    const pack = selectedPack.value;
    if (pack) return pack.diamonds + pack.bonusDiamonds;
    return Math.round((amountExclCents.value / CUSTOM_RATE_CENTS_PER_100) * 100);
  },
  set: (value: number) => {
    selectedPackId.value = 'custom';
    const count = Math.max(0, Math.round(value) || 0);
    amountExcl.value = (diamondsToCents(count) / 100).toFixed(2);
  },
});

function onAmountExclInput(): void {
  selectedPackId.value = 'custom';
}

// ─── Transaction id / status ─────────────────────────────────────────────────

const transactionId = ref('');
const status = ref<'PAID' | 'PENDING'>('PAID');

// ─── Submit ──────────────────────────────────────────────────────────────────

const saving = ref(false);
const error = ref<string | null>(null);

async function submit(): Promise<void> {
  if (!selected.value) {
    error.value = 'Pick a player first.';
    return;
  }
  if (amountExclCents.value <= 0) {
    error.value = 'Enter an amount above zero.';
    return;
  }
  if (!description.value.trim()) {
    error.value = 'A description is required.';
    return;
  }
  saving.value = true;
  error.value = null;
  try {
    await adminApi.generateInvoice({
      userId: selected.value.id,
      type: type.value,
      amountCents: amountExclCents.value,
      description: description.value.trim(),
      vatRate: vatRate.value,
      currency: CURRENCY,
      paymentMethod: paymentMethod.value,
      itemCode: selectedPack.value?.id,
      transactionId: transactionId.value.trim() || undefined,
      status: status.value,
    });
    emit('created');
  } catch (caught) {
    error.value =
      caught instanceof ApiError ? caught.message || `Request failed (${caught.status}).` : 'Could not reach the server.';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <AdminModal title="Generate invoice" wide @close="emit('close')">
    <div class="fields">
      <div class="field">
        <span>Invoice type</span>
        <div class="type-row">
          <button
            type="button"
            :class="type === 'IN' ? 'accent' : ''"
            @click="type = 'IN'"
          >Deposit</button>
          <button
            type="button"
            disabled
            title="Withdrawal invoices are disabled for now"
            :class="type === 'OUT' ? 'accent' : ''"
          >Withdrawal</button>
        </div>
      </div>
    </div>

    <section class="block">
      <h3>Player</h3>
      <div v-if="!selected" class="field">
        <span>Search by id, email or name</span>
        <input v-model="query" type="text" placeholder="Type at least 2 characters…" autocomplete="off" />
        <p v-if="searching" class="note">Searching…</p>
        <ul v-if="results.length" class="results">
          <li v-for="u in results" :key="u.id">
            <button type="button" @click="pick(u)">
              {{ u.username || u.email }} <span class="dim">({{ u.id }})</span>
            </button>
          </li>
        </ul>
        <p v-if="!creatingUser" class="note">
          Can't find them?
          <button type="button" @click="creatingUser = true">Create a new player</button>
        </p>
        <div v-else class="new-user">
          <label class="field">
            <span>Name (optional)</span>
            <input v-model="newUserName" type="text" autocomplete="off" />
          </label>
          <label class="field">
            <span>Email</span>
            <input v-model="newUserEmail" type="email" autocomplete="off" />
          </label>
          <label class="field">
            <span>Password</span>
            <input v-model="newUserPassword" type="password" autocomplete="new-password" />
          </label>
          <p v-if="createUserError" class="bad">{{ createUserError }}</p>
          <div class="new-user-actions">
            <button type="button" @click="creatingUser = false">Cancel</button>
            <button type="button" class="accent" :disabled="savingNewUser" @click="createUser">
              {{ savingNewUser ? 'Creating…' : 'Create player' }}
            </button>
          </div>
        </div>
      </div>
      <div v-else class="selected-user">
        Selected: <strong>{{ selected.username || selected.email }}</strong>
        <span class="dim">({{ selected.id }})</span>
        <button type="button" @click="clearSelection">Clear</button>
      </div>
    </section>

    <section class="block">
      <h3>Package</h3>
      <div class="pack-row">
        <button
          v-for="pack in PACKS"
          :key="pack.id"
          type="button"
          :class="selectedPackId === pack.id ? 'accent' : ''"
          @click="pickPack(pack)"
        >
          <span class="pack-name">{{ pack.name }}</span>
          <span class="pack-value">
            {{ (pack.priceCents / 100).toFixed(2) }} EUR · {{ (pack.diamonds + pack.bonusDiamonds).toLocaleString('en-US') }} 💎
          </span>
        </button>
        <button
          type="button"
          :class="selectedPackId === 'custom' ? 'accent' : ''"
          @click="pickCustom"
        >
          <span class="pack-name">Custom</span>
        </button>
      </div>
    </section>

    <div class="fields">
      <label class="field">
        <span>Payment method</span>
        <select v-model="paymentMethod">
          <option v-for="m in PAYMENT_METHODS" :key="m" :value="m">{{ m }}</option>
        </select>
      </label>
      <label class="field">
        <span>Amount (excl. VAT)</span>
        <input v-model="amountExcl" type="number" min="0" step="0.01" @input="onAmountExclInput" />
      </label>
      <label class="field">
        <span>Amount (incl. VAT)</span>
        <input v-model="amountIncl" type="number" min="0" step="0.01" />
      </label>
      <label class="field">
        <span>VAT rate</span>
        <select v-model.number="vatRate">
          <option v-for="r in VAT_RATES" :key="r" :value="r">{{ r }}%</option>
        </select>
      </label>
      <label class="field">
        <span>Diamonds</span>
        <input v-model.number="diamonds" type="number" min="0" step="100" />
      </label>
      <label class="field wide">
        <span>Description</span>
        <input v-model="description" type="text" />
      </label>
      <label class="field">
        <span>Transaction ID</span>
        <input v-model="transactionId" type="text" placeholder="Auto-generated if left blank" autocomplete="off" />
      </label>
      <label class="field">
        <span>Status</span>
        <select v-model="status">
          <option value="PAID">Paid</option>
          <option value="PENDING">Not paid</option>
        </select>
      </label>
    </div>

    <p class="note">
      Total: <strong>{{ (totalCents / 100).toFixed(2) }} {{ CURRENCY }}</strong>
      (subtotal {{ (amountExclCents / 100).toFixed(2) }} + VAT {{ (vatCents / 100).toFixed(2) }})
    </p>

    <p v-if="error" class="bad">{{ error }}</p>

    <template #footer>
      <button type="button" @click="emit('close')">Cancel</button>
      <button type="button" class="accent" :disabled="saving" @click="submit">
        {{ saving ? 'Generating…' : 'Generate invoice' }}
      </button>
    </template>
  </AdminModal>
</template>

<style scoped>
.block {
  margin: 0.9rem 0;
}
.block h3 {
  margin: 0 0 0.4rem;
  font-family: var(--font-mono);
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent);
}

.type-row,
.pack-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.pack-row button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  min-height: 2.6rem;
  padding: 0.3rem 0.6rem;
}

.pack-name {
  font-size: 0.74rem;
}

/* The price + diamond count under the pack name — thinner and muted so the
   name stays the thing read first. */
.pack-value {
  font-weight: 400;
  font-size: 0.64rem;
  color: var(--steel-400);
}
.pack-row button.accent .pack-value {
  color: var(--steel-900);
  opacity: 0.7;
}

/* The Withdrawal type button is disabled but shown, not hidden — a plain
   `cursor: default` reads as "this button just isn't doing anything right
   now", not "this option isn't available". `pane.css`'s own disabled rule
   doesn't set this, since most disabled buttons in the panel are disabled
   only transiently (a save in flight) rather than as a standing choice. */
.type-row button:disabled {
  cursor: not-allowed;
}

.results {
  margin: 0.3rem 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.2rem;
}
.results button {
  width: 100%;
  text-align: left;
}

.selected-user {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.dim {
  color: var(--steel-400);
  font-size: 0.78rem;
}

.new-user {
  margin-top: 0.5rem;
  padding: 0.6rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.25rem;
  display: grid;
  gap: 0.4rem;
}
.new-user-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
}

.field.wide {
  grid-column: 1 / -1;
}
</style>
